import * as React from "react";
import { mergeClassNames } from "./class-names";

const sizeClassNames = {
  sm: "text-body-small size-8",
  md: "text-body size-10",
  lg: "text-body-large size-12",
} as const;

const toneClassNames = {
  brand: "bg-brand-subtle text-brand-ink",
  neutral: "bg-subtle text-secondary",
} as const;

export interface MonogramProps {
  /** The record's own name. The initials are decorative: the name itself
   * must stay visible next to the monogram. */
  readonly name: string | null;
  readonly size?: keyof typeof sizeClassNames;
  readonly tone?: keyof typeof toneClassNames;
  readonly className?: string | undefined;
}

/** Derives at most two initials, counting whole characters so scripts
 * outside the Basic Multilingual Plane are not split mid-glyph. */
function initialsOf(name: string | null): string {
  const words = (name ?? "").trim().split(/\s+/u).filter(Boolean);
  const letters = words
    .slice(0, 2)
    .map((word) => Array.from(word)[0] ?? "")
    .join("");
  return letters === "" ? "—" : letters.toLocaleUpperCase();
}

/** A compact identity chip for a named record — a horse, a staff member, an
 * owner — so dense lists stay scannable without per-row photography. */
export function Monogram({
  name,
  size = "md",
  tone = "brand",
  className,
}: MonogramProps) {
  return (
    <span
      aria-hidden="true"
      data-slot="monogram"
      className={mergeClassNames(
        "inline-flex shrink-0 select-none items-center justify-center rounded-full font-semibold uppercase",
        sizeClassNames[size],
        toneClassNames[tone],
        className,
      )}
    >
      {initialsOf(name)}
    </span>
  );
}
